import { useVirtualizer } from "@tanstack/react-virtual";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useEffect, useRef, useState } from "react";

interface ComboboxProps {
  options: { value: string; label: string }[];
  selectedOption?: string;
  onOptionSelect: (option: string) => void;
  searchPlaceholder?: string;
  className?: React.HTMLAttributes<HTMLButtonElement>["className"];
}

export function Combobox({
  options,
  searchPlaceholder = "Search items...",
  onOptionSelect,
  selectedOption: _selectedOption,
  className,
}: ComboboxProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [buttonWidth, setButtonWidth] = useState(
    buttonRef.current?.clientWidth
  );
  const [selectedOption, setSelectedOption] = useState("");

  const handleSelectOption = (currentValue: string) => {
    setSelectedOption(currentValue === selectedOption ? "" : currentValue);
    onOptionSelect(currentValue);
    setOpen(false);
  };

  useEffect(
    () =>
      setSelectedOption(_selectedOption ? _selectedOption : options[0].value),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [_selectedOption]
  );

  useEffect(() => {
    const resizeW = () => setButtonWidth(buttonRef.current?.clientWidth);

    window.addEventListener("resize", resizeW); // Update the width on resize

    return () => window.removeEventListener("resize", resizeW);
  }, [buttonRef]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={buttonRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("justify-between", className)}
        >
          <p className="truncate ...">
            {selectedOption
              ? options.find((option) => option.value === selectedOption)
                  ?.value || ""
              : searchPlaceholder}
          </p>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        style={{ width: buttonWidth ? buttonWidth + "px" : "inherit" }}
      >
        <VirtualizedCommand
          options={options}
          placeholder={searchPlaceholder}
          selectedOption={selectedOption}
          onSelectOption={handleSelectOption}
        />
      </PopoverContent>
    </Popover>
  );
}
type Option = {
  value: string;
  label: string;
};

interface VirtualizedCommandProps {
  options: Option[];
  placeholder: string;
  selectedOption: string;
  onSelectOption?: (option: string) => void;
}

const VirtualizedCommand = ({
  options,
  placeholder,
  selectedOption,
  onSelectOption,
}: VirtualizedCommandProps) => {
  const parentRef = useRef(null);
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    overscan: 5,
  });

  const handleSearch = (e: React.FormEvent) => {
    const search = (e.target as HTMLInputElement).value;
    setFilteredOptions(
      options?.filter((option) =>
        option?.value.toLowerCase().includes(search.toLowerCase() ?? [])
      )
    );
  };

  return (
    <Command shouldFilter={false} onChange={handleSearch}>
      <CommandInput placeholder={placeholder} />
      <CommandList
        ref={parentRef}
        style={{
          width: "100%",
          overflow: "auto",
        }}
      >
        <CommandEmpty>No framework found.</CommandEmpty>
        <CommandGroup>
          <div
            style={{
              height: `${virtualizer?.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {virtualizer?.getVirtualItems()?.map((virtualItem) => (
              <CommandItem
                key={filteredOptions?.[virtualItem.index].value}
                value={filteredOptions?.[virtualItem.index].value}
                onSelect={(currentValue) => {
                  onSelectOption?.(currentValue);
                }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    selectedOption === filteredOptions[virtualItem.index].value
                      ? "opacity-100"
                      : "opacity-0"
                  )}
                />
                {filteredOptions?.[virtualItem.index].label}
              </CommandItem>
            ))}
          </div>
        </CommandGroup>
      </CommandList>
    </Command>
  );
};
